if (!customElements.get('ih-sticky-purchase')) {
  customElements.define('ih-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.primaryButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      if (!this.productInfo || !this.form || !this.primaryButton) return;

      this.button = this.querySelector('[data-sticky-submit]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.titleElement = this.querySelector('[data-sticky-title]');
      this.selection = this.querySelector('[data-sticky-selection]');
      this.status = this.querySelector('[data-sticky-status]');
      this.productForm = this.form.closest('product-form');
      this.mobile = matchMedia('(max-width: 900px)');
      this.events = new AbortController();
      const eventOptions = { signal: this.events.signal };
      this.scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };

      this.button.addEventListener('click', () => this.submit(), eventOptions);
      this.productInfo.addEventListener('change', this.scheduleUpdate, eventOptions);
      this.productInfo.addEventListener('input', this.scheduleUpdate, eventOptions);
      window.addEventListener('scroll', this.scheduleUpdate, { ...eventOptions, passive: true });
      window.addEventListener('resize', this.scheduleUpdate, { ...eventOptions, passive: true });
      window.visualViewport?.addEventListener('resize', this.scheduleUpdate, eventOptions);
      document.addEventListener('focusin', this.scheduleUpdate, eventOptions);
      document.addEventListener('focusout', this.scheduleUpdate, eventOptions);
      this.mobile.addEventListener('change', this.scheduleUpdate, eventOptions);

      // Product-info updates the real form, price, and options after a variant change.
      // Ignore this element's own rendering to avoid a mutation feedback loop.
      this.productObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.scheduleUpdate();
      });
      this.productObserver.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class', 'value', 'open'],
      });

      this.overlayObserver = new MutationObserver(this.scheduleUpdate);
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('.ih-header, cart-drawer, cart-notification').forEach((element) => {
        this.overlayObserver.observe(element, {
          subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-expanded', 'open'],
        });
      });
      this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
      this.resizeObserver.observe(this.primaryButton);
      this.resizeObserver.observe(this);
      const existingPadding = document.documentElement.style.getPropertyValue('--ih-purchase-original-padding');
      document.documentElement.style.setProperty('--ih-purchase-original-padding', existingPadding || getComputedStyle(document.body).paddingBottom);
      this.update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.productObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      if (!document.querySelector('ih-sticky-purchase')) {
        document.documentElement.classList.remove('ih-mobile-purchase-enabled');
        document.documentElement.style.removeProperty('--ih-purchase-space');
        document.documentElement.style.removeProperty('--ih-purchase-original-padding');
      }
    }

    quantityInput() {
      return Array.from(this.form.elements).find((element) => element.name === 'quantity');
    }

    hasOverlay() {
      return Boolean(document.querySelector(
        '.ih-menu-button[aria-expanded="true"], cart-drawer.active, #cart-notification.active, ' +
        'dialog[open], modal-dialog[open], product-modal[open], quick-add-modal[open], ' +
        '.product-media-modal[open], .menu-opening'
      )) || document.body.classList.contains('overflow-hidden');
    }

    update() {
      if (!this.isConnected || !this.primaryButton.isConnected) return;
      const variantId = this.form.querySelector('[name="id"]')?.value;
      const loading = this.primaryButton.classList.contains('loading');
      const disabled = this.primaryButton.disabled || this.primaryButton.getAttribute('aria-disabled') === 'true';
      const primaryLabel = this.primaryButton.querySelector('span')?.textContent.trim() || this.label.textContent;
      const quantity = this.quantityInput()?.value || '1';
      let variantTitle = '';
      try {
        const variant = JSON.parse(this.productInfo.querySelector('[data-selected-variant]')?.textContent || 'null');
        if (variant?.title && variant.title !== 'Default Title') variantTitle = variant.title;
      } catch { /* A pending option update can temporarily omit the selected variant. */ }

      const priceContainer = document.getElementById(`price-${this.dataset.sectionId}`);
      const livePrice = priceContainer?.querySelector('.price--on-sale .price-item--sale') ||
        priceContainer?.querySelector('.price__regular .price-item--regular');
      if (livePrice) this.setText(this.price, livePrice.textContent.trim());
      const selection = [variantTitle, Number(quantity) > 1 ? `${this.dataset.quantityLabel}: ${quantity}` : ''].filter(Boolean).join(' · ');
      this.setText(this.selection, selection);
      this.selection.hidden = !selection;
      this.setText(this.label, loading ? this.dataset.loadingLabel : primaryLabel);
      this.button.disabled = disabled || !variantId;
      this.button.setAttribute('aria-busy', String(loading));
      this.button.setAttribute('aria-label', [this.label.textContent, this.titleElement.textContent, variantTitle, `${this.dataset.quantityLabel}: ${quantity}`, this.price.textContent].filter(Boolean).join(', '));
      if (document.querySelector('cart-drawer, cart-notification')) this.button.setAttribute('aria-haspopup', 'dialog');
      this.setText(this.status, loading ? this.dataset.loadingLabel : '');

      const overlay = this.hasOverlay();
      const activeInput = document.activeElement;
      const editing = activeInput?.matches('textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="hidden"])');
      const headerBottom = Math.max(0, document.querySelector('.ih-header-section')?.getBoundingClientRect().bottom || 0);
      const buttonRect = this.primaryButton.getBoundingClientRect();
      // A button below the fold must never be mistaken for a button passed on scroll.
      const passedButton = buttonRect.height > 0 && buttonRect.bottom <= headerBottom;
      const enabled = this.mobile.matches && Boolean(variantId);
      const visible = enabled && passedButton && !overlay && !editing;
      this.hidden = !visible;
      document.documentElement.classList.toggle('ih-mobile-purchase-enabled', enabled);
      if (visible) document.documentElement.style.setProperty('--ih-purchase-space', `${Math.ceil(this.getBoundingClientRect().height)}px`);

      if (overlay && this.submittedFromSticky) this.restoreFocusAfterOverlay = true;
      if (visible && this.wasOverlay && !overlay && this.restoreFocusAfterOverlay) {
        if (document.activeElement === document.body || document.activeElement?.closest('cart-drawer, cart-notification')) {
          this.button.focus({ preventScroll: true });
        }
        this.restoreFocusAfterOverlay = false;
        this.submittedFromSticky = false;
      }
      this.wasOverlay = overlay;

      const error = this.productForm.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (error && this.submittedFromSticky) this.revealError(error);
    }

    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    submit() {
      this.update();
      if (this.button.disabled || this.hidden) return;
      const quantity = this.quantityInput();
      if (quantity && !quantity.checkValidity()) {
        quantity.scrollIntoView({ block: 'center', behavior: 'auto' });
        quantity.focus({ preventScroll: true });
        quantity.reportValidity();
        return;
      }
      // Some native request failures only dispatch the standard cart error event.
      // Surface those through the same visible error area as inventory errors.
      if (!this.errorEventAttached && window.StandardEvents?.CartErrorEvent) {
        const eventType = new window.StandardEvents.CartErrorEvent({ error: '', code: 'SERVICE_UNAVAILABLE' }).type;
        this.productForm.addEventListener(eventType, () => {
          if (!this.submittedFromSticky) return;
          const error = this.productForm.querySelector('.product-form__error-message-wrapper');
          if (error?.hidden) this.productForm.handleErrorMessage(this.dataset.errorMessage);
          this.scheduleUpdate();
        }, { signal: this.events.signal });
        this.errorEventAttached = true;
      }
      this.submittedFromSticky = true;
      // Includes external form-associated quantity controls and any app properties.
      this.form.requestSubmit(this.primaryButton);
      this.scheduleUpdate();
    }

    revealError(error) {
      this.submittedFromSticky = false;
      this.restoreFocusAfterOverlay = false;
      error.setAttribute('tabindex', '-1');
      error.scrollIntoView({ block: 'center', behavior: 'auto' });
      error.focus({ preventScroll: true });
      this.scheduleUpdate();
    }
  });
}
